import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { HeroSection as HeroSectionType } from "../types";

interface HeroSectionProps {
  data: HeroSectionType;
}

const HeroSection = ({ data }: HeroSectionProps) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentFullText = data.typingTexts[currentTextIndex];
    const typingSpeed = isDeleting ? 50 : 100;

    const timer = setTimeout(() => {
      if (!isDeleting) {
        if (displayedText.length < currentFullText.length) {
          setDisplayedText(currentFullText.slice(0, displayedText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (displayedText.length > 0) {
          setDisplayedText(displayedText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setCurrentTextIndex((prev) => (prev + 1) % data.typingTexts.length);
        }
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentTextIndex, data.typingTexts]);

  const handleWhatsAppClick = () => {
    window.open("https://wa.me/qr/MIPMGLGF4RZ5O1", "_blank");
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5"></div>

      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: "1s" }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-8 animate-fade-in">
          <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
          <span className="text-sm font-mono text-primary">Available for Collaboration</span>
        </div>

        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-foreground mb-6 animate-fade-in-up">
          {data.title}
        </h1>

        <div className="h-20 sm:h-24 mb-8">
          <p className="text-2xl sm:text-3xl lg:text-4xl font-mono text-primary animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
            &lt; {displayedText}
            <span className="inline-block w-1 h-8 bg-primary ml-1 animate-blink"></span>
            /&gt;
          </p>
        </div>

        <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-12 animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
          {data.subtitle}
        </p>

        <div className="flex flex-wrap items-center justify-center gap-4 animate-fade-in-up" style={{ animationDelay: "0.6s" }}>
          {data.ctaButtons.map((button) => (
            button.action === "whatsapp" ? (
              <Button
                key={button.id}
                variant={button.variant}
                size="lg"
                iconName={button.icon}
                iconPosition="left"
                onClick={handleWhatsAppClick}
                className="shadow-elevation-2 hover:shadow-elevation-3 transition-all duration-300"
              >
                {button.label}
              </Button>
            ) : (
              <Link key={button.id} to={button.href || "#"}>
                <Button
                  variant={button.variant}
                  size="lg"
                  iconName={button.icon}
                  iconPosition="left"
                  className="shadow-elevation-2 hover:shadow-elevation-3 transition-all duration-300"
                >
                  {button.label}
                </Button>
              </Link>
            )
          ))}
        </div>

        <div className="mt-16 flex items-center justify-center gap-6 animate-fade-in-up" style={{ animationDelay: "0.8s" }}>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="MapPin" size={18} />
            <span className="text-sm">Orlando, FL</span>
          </div>
          <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="GraduationCap" size={18} />
            <span className="text-sm">Valencia College</span>
          </div>
          <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="Code2" size={18} />
            <span className="text-sm">Computer Science</span>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <Icon name="ChevronDown" size={32} color="var(--color-muted-foreground)" />
      </div>
    </section>
  );
};

export default HeroSection;